/**
 * Onboarding: bootstrap state, the user-profile submit, and the per-company
 * "findings" question loop that gathers what Reeve needs about the business.
 *
 * All endpoints are tenant-scoped (the server resolves the tenant from the
 * token). GET /api/onboarding/state additionally has a side effect: it
 * idempotently ensures the caller's Tenant row exists.
 *
 * "complete" is inferred, not a stored flag:
 *   userProfileComplete && companyCount >= 1 && companyOnboardingComplete.
 */

import { apiGet, apiPost, apiPut } from '../apiClient';
import type { EntityQuestionValue } from './entityQuestions';
import type { MeDto } from './me';

export interface OnboardingState {
  userProfileComplete: boolean;
  companyCount: number;
  primaryCompanyId: string | null;
  companyOnboardingComplete: boolean;
  complete: boolean;
}

/** GET /api/onboarding/state — also ensures the Tenant row exists (idempotent). */
export function getOnboardingState(): Promise<OnboardingState> {
  return apiGet<OnboardingState>('/api/onboarding/state');
}

export interface UpdateProfileRequest {
  firstName: string;
  lastName: string;
  role: string;
}

/** PUT /api/users/me — sets the user's firstName/lastName/role. */
export function updateUserProfile(request: UpdateProfileRequest): Promise<MeDto> {
  return apiPut<MeDto>('/api/users/me', request);
}

/**
 * Question kind. NOTE the camelCase on the wire: the long-text kind is
 * "longText" (capital T), which the wizard maps to a <textarea>.
 */
export type OnboardingQuestionKind = 'text' | 'longText' | 'number' | 'boolean' | 'choice';

export interface OnboardingQuestionOption {
  value: string;
  label: string;
}

export interface OnboardingQuestion {
  key: string;
  prompt: string;
  kind: OnboardingQuestionKind;
  /** Present for "choice" questions. */
  options?: OnboardingQuestionOption[] | null;
  /** Optional helper text shown under the prompt. */
  help?: string | null;
  /**
   * Which capture SCREEN this question belongs on, from the catalog. Absent for the free-text profile
   * questions, which are asked one at a time; its presence is how the wizard knows it has reached the
   * grouped predicate phase.
   */
  group?: string | null;
}

/** One capture screen: a heading and the questions that currently apply on it. */
export interface QuestionGroup {
  key: string;
  title: string;
  /** One line under the heading saying what the screen covers. From the catalog, so re-grouping the
   * questions in data re-describes the screen too. */
  blurb: string;
  questions: EntityQuestionValue[];
}

export interface GroupedQuestions {
  groups: QuestionGroup[];
}

/**
 * Every applicable employer question, sorted into its screen. Re-fetch after each answer: the server
 * recomputes applicability, so this is also what reveals the questions an answer just unlocked.
 */
export function getGroupedQuestions(companyId: string): Promise<GroupedQuestions> {
  return apiGet<GroupedQuestions>(`/api/companies/${companyId}/onboarding/questions`);
}

/** Both the "next" and "answer" endpoints return this shape, so the client can chain. */
export interface NextQuestion {
  done: boolean;
  question?: OnboardingQuestion | null;
}

/** GET /api/companies/{id}/onboarding/next — the first unanswered question, or done. */
export function getNextQuestion(companyId: string): Promise<NextQuestion> {
  return apiGet<NextQuestion>(`/api/companies/${companyId}/onboarding/next`);
}

/**
 * POST /api/companies/{id}/onboarding/answer — stores the answer and returns the
 * next question (or done). Values are always sent as strings (numbers/choices/
 * booleans stringified by the caller).
 */
export function answerQuestion(
  companyId: string,
  key: string,
  value: string,
): Promise<NextQuestion> {
  return apiPost<NextQuestion>(`/api/companies/${companyId}/onboarding/answer`, { key, value });
}
